import React from "react";
import { Helmet } from "react-helmet";
import { graphql, useStaticQuery } from "gatsby";
import { normalizeUrl } from "@qlik/utils/normalizePathOrUrl";
import { useAppContext } from "@qlik/qlik/src/context/AppContext";
import { useI18n } from "@qlik/utils/i18nContext";

/**
 * SEO component for managing page metadata and SEO attributes.
 * Uses React-Helmet to update head elements.
 *
 * **Note - Helmet is able to toggle body attributes dynamically at runtime with
 * React Context, as Gatsby Head API cannot - `Gatsby@v5.12.12`.**
 *
 * @param {Object} data - Data object containing metadata information.
 * @param {string} pageSlug - Slug of the page.
 * @returns {JSX.Element} JSX element containing SEO metadata.
 */
const Seo = ({ data, pageSlug = "" }) => {
    const {
        metaTitle = "",
        metaRobots: robots = [],
        googleBot = [],
        bingBot = [],
        canonicalUrl = {},
        canonicalUrlOverride = "",
        metaImage = [],
        metadata = [],
    } = data || {};

    const { i18n, translatedPaths } = useI18n();

    const description = data?.metaDescription?.metaDescription ?? "";

    const additionalMetadata = metadata?.reduce((acc, { keyType, key, values }) => {
        if (keyType === "name" || keyType === "property") {
            acc.push({
                [keyType]: key,
                content: values?.values || "",
            });
        }
        return acc;
    }, []);

    const { site } = useStaticQuery(
        graphql`
            query {
                site {
                    siteMetadata {
                        title
                        description
                        siteUrl
                        author
                        siteName
                        robots
                        googleBot
                        bingBot
                        metaImagePath
                        metaImageHeight
                        metaImageWidth
                    }
                }
            }
        `
    );

    // Title variable
    const pageTitle = metaTitle || site.siteMetadata.title;

    // MetaDescription variable , check if the value is passed to the Seo function
    // If there is no value passed, use the default from gatsby-config.js
    const metaDescription = description || site.siteMetadata.description;

    // Meta robots variables, check if the values is null
    // If there is null value passed, use the default values from gatsby-config.js
    const metaRobots = robots ? robots.join(", ") : site.siteMetadata.robots.join(", ");
    const metaGoogleBot = googleBot ? googleBot.join(", ") : site.siteMetadata.googleBot.join(", ");
    const metaBingBot = bingBot ? bingBot.join(", ") : site.siteMetadata.bingBot.join(", ");

    const { bodyClassName } = useAppContext();

    // Meta Image
    const mImage = metaImage?.[0]?.secure_url ?? `${site.siteMetadata.siteUrl}/${site.siteMetadata.metaImagePath}`;
    const mImageSize = {
        height: metaImage?.[0]?.height || site.siteMetadata.metaImageHeight,
        width: metaImage?.[0]?.width || site.siteMetadata.metaImageWidth,
    };

    // Normalize the page URL
    // if the pageSlug starts with "blog/" then we do not want to get the lang prefixed path
    pageSlug = pageSlug.startsWith("blog/") ? pageSlug : i18n.getLangPrefixedPath(pageSlug);
    const pageUrl = normalizeUrl(`${site.siteMetadata.siteUrl}/${pageSlug}`);

    // Check if canonicalUrlOverride field exists, if not
    // Check if canonicalUrlSlug field exists, if not
    // canonical is null
    const canonical = canonicalUrlOverride || canonicalUrl?.slug || null;

    // Array for link meta tags
    const linkArr = [];

    // Adding canonical <link>
    canonical &&
        linkArr.push({
            rel: "canonical",
            href: canonical,
        });

    // Produce hrefLang <link> tags based on present languages and pull the related paths from the context
    const hrefLangTags = Object.keys(translatedPaths).map((langCode) => {
        return {
            rel: "alternate",
            href: normalizeUrl(`${site.siteMetadata.siteUrl}/${translatedPaths[langCode]}`),
            hrefLang: langCode,
        };
    });

    // Add x-default <link> tag
    if ("en" in translatedPaths) {
        hrefLangTags.push({
            rel: "alternate",
            href: normalizeUrl(`${site.siteMetadata.siteUrl}/${translatedPaths.en}`),
            hrefLang: "x-default",
        });
    }

    // Update the <link> array for Helmet to build
    linkArr.push(...hrefLangTags);

    return (
        <Helmet
            title={pageTitle}
            link={linkArr}
            meta={[
                {
                    name: `description`,
                    content: metaDescription,
                },
                {
                    name: `robots`,
                    content: metaRobots,
                },
                {
                    name: `googlebot`,
                    content: metaGoogleBot,
                },
                {
                    name: `bingbot`,
                    content: metaBingBot,
                },
                {
                    property: `og:type`,
                    content: `website`,
                },
                {
                    property: `og:title`,
                    content: pageTitle,
                },
                {
                    property: `og:description`,
                    content: metaDescription,
                },
                {
                    property: `og:url`,
                    content: pageUrl || site.siteMetadata.siteUrl,
                },
                {
                    property: `og:site_name`,
                    content: site.siteMetadata.siteName,
                },
                {
                    name: `twitter:creator`,
                    content: site.siteMetadata.author,
                },
                {
                    name: `twitter:title`,
                    content: pageTitle,
                },
                {
                    name: `twitter:description`,
                    content: metaDescription,
                },
            ]
                .concat(
                    mImage
                        ? [
                              {
                                  property: "og:image",
                                  content: mImage,
                              },
                              {
                                  property: "og:image:width",
                                  content: mImageSize.width,
                              },
                              {
                                  property: "og:image:height",
                                  content: mImageSize.height,
                              },
                              {
                                  name: "twitter:card",
                                  content: metaTitle === site.siteMetadata.title ? "summary_large_image" : "summary",
                              },
                          ]
                        : [
                              {
                                  name: "twitter:card",
                                  content: "summary",
                              },
                          ]
                )
                .concat(Array.isArray(additionalMetadata) ? additionalMetadata : [])}
            bodyAttributes={{
                class: bodyClassName,
            }}
        />
    );
};

export default Seo;
